/**
 * What a downhole component WRITE carries, once — for the Add / Edit form and
 * the CSV import alike (fieldiq-api#900, `POST /downhole/components`,
 * `/components/import`, `PATCH /components/:id`).
 *
 * THE API'S KEYS, AND ONLY THEM. The route refuses an unknown key with a 400
 * ("a dropped key is a silent no-op"), so this list is the route's
 * COMPONENT_FIELDS + OPENING_FIELDS, not the columns a component READ returns.
 * `connection_type` is vestigial and is not here; nor are the disassembly
 * fields (dope/clean, thread condition, turnaround), which only a teardown
 * writes.
 *
 * OPENING BALANCES — total_run_hours, total_runs, total_footage_ft,
 * last_inspection — are create and import only. After that they move through
 * runs and inspections; a PATCH carrying one is a 400.
 *
 * UNITS. Every measure is sent IMPERIAL, as stored. The form and the CSV read
 * a measure in the TENANT's system and convert it with `toStoredImperial`
 * (lib/downhole-units.ts). The single exception is a CSV column whose header
 * names an imperial unit (`length_ft`, `weight_lbs` …): that column is read in
 * the unit its name says, whatever the tenant's system.
 *
 * ABSENT IS NOT ZERO. A blank cell or input sends nothing on create, and an
 * edit that clears a field sends `null`. A wear limit of 0 is refused by the
 * API (0 is "untracked" spelled wrong); blank is how a limit is left untracked.
 */
import Papa from 'papaparse';
import { DH_CATEGORY_LABEL } from './downhole-labels';
import { dhUnitLabel, toStoredImperial, type DhQuantity } from './downhole-units';
import { DH_COMPONENT_CATEGORIES, type DhUnitSystem } from '../types/downhole';

export type DhInputKind = 'text' | 'number' | 'integer' | 'day';

export interface DhComponentInputField {
  /** The API's key. */
  key: string;
  label: string;
  kind: DhInputKind;
  /** A measured value: converted between the tenant's system and imperial. */
  quantity?: DhQuantity;
  /** An unconverted unit shown beside the label (hours, runs). */
  unit?: string;
  /** Create and import only (an opening balance). */
  opening?: boolean;
  required?: boolean;
  /** The neutral CSV header, read in the tenant's units. Defaults to `key`. */
  csv?: string;
  /** A CSV header that names an imperial unit, so its column is read as imperial. */
  imperialAlias?: string;
  maxLength?: number;
  textarea?: boolean;
}

export const DH_COMPONENT_INPUT_FIELDS: DhComponentInputField[] = [
  { key: 'serial_number', label: 'Serial number', kind: 'text', required: true, maxLength: 50 },
  { key: 'category', label: 'Category', kind: 'text', required: true },
  { key: 'description', label: 'Description', kind: 'text', required: true, maxLength: 2000 },
  { key: 'od', label: 'OD', kind: 'number', quantity: 'diameter' },
  { key: 'id_inner', label: 'ID', kind: 'number', quantity: 'diameter', csv: 'id' },
  { key: 'length_ft', label: 'Length', kind: 'number', quantity: 'length', csv: 'length', imperialAlias: 'length_ft' },
  { key: 'weight_lbs', label: 'Weight', kind: 'number', quantity: 'weight', csv: 'weight', imperialAlias: 'weight_lbs' },
  { key: 'connection_top', label: 'Connection top', kind: 'text', maxLength: 50 },
  { key: 'connection_bottom', label: 'Connection bottom', kind: 'text', maxLength: 50 },
  { key: 'fish_neck_od', label: 'Fish neck OD', kind: 'number', quantity: 'diameter' },
  { key: 'max_temp_f', label: 'Max temperature', kind: 'number', quantity: 'temperature', csv: 'max_temp', imperialAlias: 'max_temp_f' },
  { key: 'max_wob_klbs', label: 'Max WOB', kind: 'number', quantity: 'force', csv: 'max_wob', imperialAlias: 'max_wob_klbs' },
  { key: 'pressure_rating', label: 'Pressure rating', kind: 'number', quantity: 'pressure' },
  { key: 'manufacturer', label: 'Manufacturer', kind: 'text', maxLength: 100 },
  { key: 'model', label: 'Model', kind: 'text', maxLength: 100 },
  { key: 'year_manufactured', label: 'Year built', kind: 'integer' },
  { key: 'date_manufactured', label: 'Date manufactured', kind: 'day' },
  { key: 'wear_limit_hours', label: 'Hours limit', kind: 'number', unit: 'h' },
  { key: 'wear_limit_runs', label: 'Runs limit', kind: 'integer', unit: 'runs' },
  { key: 'next_inspection_due', label: 'Next inspection due', kind: 'day' },
  { key: 'location', label: 'Location', kind: 'text', maxLength: 100 },
  { key: 'notes', label: 'Notes', kind: 'text', maxLength: 10000, textarea: true },
  { key: 'total_run_hours', label: 'Run hours to date', kind: 'number', unit: 'h', opening: true },
  { key: 'total_runs', label: 'Runs to date', kind: 'integer', unit: 'runs', opening: true },
  { key: 'total_footage_ft', label: 'Footage to date', kind: 'number', quantity: 'length', opening: true, csv: 'total_footage', imperialAlias: 'total_footage_ft' },
  { key: 'last_inspection', label: 'Last inspection', kind: 'day', opening: true },
];

/** The unit a field's input is typed in for this tenant, or null for a unitless field. */
export function inputUnit(field: DhComponentInputField, system: DhUnitSystem | null | undefined): string | null {
  if (field.quantity) return dhUnitLabel(field.quantity, system);
  return field.unit ?? null;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * One typed value → what the API is sent, or a message. `''` is absent and
 * comes back as `{ value: null }`; the caller decides whether absent is
 * "send nothing" (create) or "clear it" (an edit that emptied the field).
 */
export function parseInputValue(
  field: DhComponentInputField,
  raw: string,
  system: DhUnitSystem | null | undefined,
  readAsImperial = false,
): { value: string | number | null } | { error: string } {
  const text = raw.trim();
  if (text === '') return { value: null };
  if (field.kind === 'text') return { value: text };
  if (field.kind === 'day') {
    return DAY.test(text) ? { value: text } : { error: `${field.label} must be a date (YYYY-MM-DD)` };
  }
  // No thousands separators: "1,250" is 1250 in one country and 1.25 in the
  // next, and a guess between them is a wrong number that looks right.
  if (!/^-?\d+(\.\d+)?$/.test(text)) return { error: `${field.label} must be a number, written without separators` };
  if (field.kind === 'integer') {
    return Number.isInteger(Number(text)) ? { value: Number(text) } : { error: `${field.label} must be a whole number` };
  }
  if (!field.quantity) return { value: Number(text) };
  return { value: toStoredImperial(text, field.quantity, readAsImperial ? 'imperial' : system) };
}

/** A category typed as its key (`drill_collar`) or its label (`Drill Collar`), any case. */
export function categoryFromText(text: string): string {
  const t = text.trim();
  const lower = t.toLowerCase();
  const key = lower.replace(/[\s-]+/g, '_');
  if ((DH_COMPONENT_CATEGORIES as readonly string[]).includes(key)) return key;
  const byLabel = Object.entries(DH_CATEGORY_LABEL).find(([, label]) => label.toLowerCase() === lower);
  // Unrecognised: sent as written, so the API's refusal names it and lists the
  // 22 it accepts. Never mapped to `other`, which is a real category.
  return byLabel ? byLabel[0] : t;
}

// ─── CSV import ─────────────────────────────────────────────────────────────

/** The API's per-call ceiling (DH_IMPORT_MAX_ROWS). */
export const DH_IMPORT_MAX_ROWS = 500;

/** A header cell folded for matching: lower case, spaces and hyphens to `_`. */
const foldHeader = (h: string) => h.trim().toLowerCase().replace(/[\s-]+/g, '_');

/**
 * The header row a tenant downloads as a template: the neutral name, with the
 * unit it is read in, in brackets — `od (mm)` for a metric tenant, `od (in)`
 * for an imperial one.
 */
export function csvTemplateHeaders(system: DhUnitSystem | null | undefined): string[] {
  return DH_COMPONENT_INPUT_FIELDS.map((f) => {
    const name = f.csv ?? f.key;
    const unit = f.quantity ? dhUnitLabel(f.quantity, system) : null;
    return unit ? `${name} (${unit})` : name;
  });
}

interface ColumnBinding {
  field: DhComponentInputField;
  imperial: boolean;
}

export interface ParsedComponentCsv {
  /** Row bodies, in the order the API will number them (rows[i]). */
  rows: Record<string, unknown>[];
  /** The spreadsheet line each row came from (header is line 1). rows[i] ↔ lines[i]. */
  lines: number[];
  /** Problems found before anything was sent. Any entry here means nothing is sent. */
  errors: { line: number | null; error: string }[];
}

/**
 * A CSV file → the import body. Every problem is collected, not just the
 * first, because the API's own validation works the same way and a reader
 * fixing a file wants the whole list once.
 *
 * Header rules:
 *   - a neutral name (`length`) is read in the tenant's units; a bracketed
 *     unit after it (`length (m)`) must BE the tenant's unit, or the file is
 *     refused rather than read in the wrong one;
 *   - an imperial-suffixed name (`length_ft`) is read as imperial;
 *   - an unknown header is refused. A column dropped on the floor is data the
 *     reader believes they imported.
 */
export function parseComponentCsv(text: string, system: DhUnitSystem | null | undefined): ParsedComponentCsv {
  const errors: ParsedComponentCsv['errors'] = [];
  // Blank lines are kept through the parse and dropped below, so the line a
  // row is reported at is the line the reader sees in their spreadsheet
  // (papaparse's skipEmptyLines renumbers every row after a gap).
  const parsed = Papa.parse<string[]>(text.replace(/^﻿/, ''));
  for (const e of parsed.errors) {
    // A ragged row (more or fewer cells than the header) is read cell by cell
    // below; every other parse problem is reported.
    if (e.type === 'FieldMismatch') continue;
    errors.push({ line: typeof e.row === 'number' ? e.row + 1 : null, error: e.message });
  }
  const isBlank = (cells: string[]) => cells.every((c) => String(c ?? '').trim() === '');
  const numbered = parsed.data.map((cells, i) => ({ cells, line: i + 1 })).filter((r) => !isBlank(r.cells));
  const header = numbered[0]?.line === 1 ? numbered[0].cells : undefined;
  const body = numbered.slice(header ? 1 : 0);
  if (!header) {
    return {
      rows: [],
      lines: [],
      errors: [{ line: null, error: numbered.length ? 'The first line must be the header row.' : 'The file is empty.' }],
    };
  }

  const bindings: (ColumnBinding | null)[] = header.map((cell) => {
    const raw = String(cell ?? '');
    const unitMatch = raw.match(/\(([^)]*)\)\s*$/);
    const name = foldHeader(unitMatch ? raw.slice(0, unitMatch.index) : raw);
    if (!name) return null;
    const alias = DH_COMPONENT_INPUT_FIELDS.find((f) => f.imperialAlias === name);
    const field = alias ?? DH_COMPONENT_INPUT_FIELDS.find((f) => (f.csv ?? f.key) === name || f.key === name);
    if (!field) {
      errors.push({ line: 1, error: `Unknown column “${raw.trim()}”` });
      return null;
    }
    const imperial = !!alias;
    if (unitMatch) {
      const written = unitMatch[1].trim().toLowerCase();
      const expected = field.quantity ? dhUnitLabel(field.quantity, imperial ? 'imperial' : system).toLowerCase() : null;
      if (!expected || written !== expected) {
        errors.push({
          line: 1,
          error: expected
            ? `Column “${raw.trim()}” is read in ${expected}; rename it or convert the column`
            : `Column “${raw.trim()}” takes no unit`,
        });
      }
    }
    return { field, imperial };
  });

  const seen = new Set<string>();
  bindings.forEach((b) => {
    if (!b) return;
    if (seen.has(b.field.key)) errors.push({ line: 1, error: `${b.field.label} appears in more than one column` });
    seen.add(b.field.key);
  });
  for (const f of DH_COMPONENT_INPUT_FIELDS.filter((x) => x.required)) {
    if (!seen.has(f.key)) errors.push({ line: 1, error: `The file has no ${f.csv ?? f.key} column` });
  }

  if (body.length === 0) errors.push({ line: null, error: 'The file has a header row and no components.' });
  if (body.length > DH_IMPORT_MAX_ROWS) {
    errors.push({ line: null, error: `At most ${DH_IMPORT_MAX_ROWS} components per import; this file has ${body.length}. Split it.` });
  }

  const rows: Record<string, unknown>[] = [];
  const lines: number[] = [];
  body.forEach(({ cells, line }) => {
    const row: Record<string, unknown> = {};
    bindings.forEach((b, col) => {
      if (!b) return;
      const cell = String(cells[col] ?? '');
      if (b.field.key === 'category') {
        if (cell.trim()) row.category = categoryFromText(cell);
        return;
      }
      const out = parseInputValue(b.field, cell, system, b.imperial);
      if ('error' in out) errors.push({ line, error: out.error });
      else if (out.value !== null) row[b.field.key] = out.value;
    });
    rows.push(row);
    lines.push(line);
  });

  return { rows, lines, errors };
}
